import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Breadcrumbs, Link as MuiLink } from '@mui/material';
import QuizComponent from '../components/QuizComponent';
import { UserContext } from '../contexts/UserContext';
import { content, progress } from '../api/client';

const STORAGE_KEY = (moduleId, courseId) => `quiz_${moduleId}_${courseId}`;

const CourseQuiz = () => {
  const { moduleId, courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseTitle, setCourseTitle] = useState('');
  const [moduleTitle, setModuleTitle] = useState('');
  const [modules, setModules] = useState([]);

  const storageKey = STORAGE_KEY(moduleId, courseId);
  const savedResult = JSON.parse(localStorage.getItem(storageKey) || 'null');

  useEffect(() => {
    Promise.all([content.quiz(moduleId, courseId), content.modules()])
      .then(([quizRes, modsRes]) => {
        setQuiz(quizRes.data);
        setModules(modsRes.data);
        const mod = modsRes.data.find(m => m.id === moduleId);
        const course = mod?.courses?.find(c => c.id === courseId);
        setModuleTitle(mod?.title || moduleId);
        setCourseTitle(course?.title || courseId);
      })
      .catch(() => setError('Quiz not found for this course'))
      .finally(() => setLoading(false));
  }, [moduleId, courseId]);

  const saveQuizResult = (score, passed) => {
    localStorage.setItem(storageKey, JSON.stringify({ score, passed }));
    if (user) {
      progress.quiz({ course_id: courseId, passed, score }).catch(() => {});
    }
  };

  const getNextDestination = () => {
    const mod = modules.find(m => m.id === moduleId);
    const courses = mod?.courses || [];
    const idx = courses.findIndex(c => c.id === courseId);
    const next = courses[idx + 1];
    return next
      ? `/module/${moduleId}/course/${next.id}`
      : `/module/${moduleId}/complete`;
  };

  const handleComplete = (answers, score, passed) => {
    saveQuizResult(score, passed);
    navigate(`/module/${moduleId}/course/${courseId}/quiz/results`, {
      state: {
        quiz,
        answers,
        score,
        passed,
        moduleId,
        courseId,
        courseTitle,
        moduleTitle,
        nextDestination: getNextDestination()
      }
    });
  };

  const handleContinue = () => {
    navigate(getNextDestination());
  };

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink underline="hover" color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
          Modules
        </MuiLink>
        <MuiLink underline="hover" color="inherit" onClick={() => navigate(`/module/${moduleId}`)} sx={{ cursor: 'pointer' }}>
          {moduleTitle}
        </MuiLink>
        <MuiLink underline="hover" color="inherit" onClick={() => navigate(`/module/${moduleId}/course/${courseId}`)} sx={{ cursor: 'pointer' }}>
          {courseTitle}
        </MuiLink>
        <Typography color="text.primary">Quiz</Typography>
      </Breadcrumbs>

      <Typography variant="h4" fontWeight="bold" align="center" gutterBottom>
        Course Quiz
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" align="center" gutterBottom>
        {courseTitle}
      </Typography>

      <Box mt={3}>
        <QuizComponent
          quiz={quiz}
          onComplete={handleComplete}
          alreadyPassed={savedResult?.passed}
          savedScore={savedResult?.score}
          onContinue={handleContinue}
        />
      </Box>
    </Box>
  );
};

export default CourseQuiz;
